import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContent, getWorkflowConfig, WORKFLOW_MATRIX, getWorkflowId } from '../../context/ContentContext';
import { useModalWithData } from '../../hooks/useModal';
import { CreateContentModal } from '../../components/modals/CreateContentModal/CreateContentModal';
import { EmailSettingsModal } from '../../components/modals/EmailSettingsModal/EmailSettingsModal';
import { IoMdAddCircleOutline } from "react-icons/io";
import { GetLatestWorkflowsRun } from "../../services/GetLatestWorkflowsRun";
import { MdDelete, MdCheckCircle, MdClose } from "react-icons/md";
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from "react-icons/fi";
import { FaArrowRight } from "react-icons/fa6";
import './ContentList.css';

const EXCLUDED_NODE_NAMES = new Set(['start', 'end', 'if else', 'human feedback']);

const normalizeNodeName = (name) => name
  ? name
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/#/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  : '';

const extractOrderedRunNodes = (runItem) => {
  const seen = new Set();
  const messages = Array.isArray(runItem?.outputs?.messages) ? runItem.outputs.messages : [];

  return messages.reduce((acc, message) => {
    const rawName = typeof message?.name === 'string' ? message.name : '';
    const normalized = normalizeNodeName(rawName);

    if (!rawName || EXCLUDED_NODE_NAMES.has(normalized)) {
      return acc;
    }

    const nodeTitle = rawName.replace(/_/g, ' ').trim();
    if (!nodeTitle || seen.has(nodeTitle)) {
      return acc;
    }

    seen.add(nodeTitle);
    acc.push({ id: nodeTitle, label: nodeTitle });
    return acc;
  }, []);
};

export const ContentListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { deleteWorkflowRun, setRunStateForDownload, setRunStateForFeedback, startRun } = useContent();
  const createModal = useModalWithData();
  const emailModal = useModalWithData();

  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 5;

  const WORKFLOW_IDS = Object.values(WORKFLOW_MATRIX).filter(Boolean);
  
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, runId: null });
  const [viewFullModal, setViewFullModal] = useState({ show: false, content: '' });
  const [loadingExploreId, setLoadingExploreId] = useState(null);

  useEffect(() => {
    if (location.state?.reopenModal && location.state?.editConfig) {
      createModal.open(location.state.editConfig);

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate]); 

  const showToast = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 4000);
  };

  const handleEmailStartRun = async (emailSettings) => {
    const contentData = emailModal.data;
    const fullConfig = {
      ...contentData,
      emailSettings,
    };

    const workflowId = getWorkflowId(
      contentData.compliance,
      'email',
      emailSettings.template
    );

    startRun({ ...fullConfig, workflowId });
    
    navigate('/run-progress');
  };

  useEffect(() => {
    fetchHistory(currentPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  const fetchHistory = async (page) => {
    try {
      setIsLoading(true);
      const offset = (page - 1) * itemsPerPage;
      const response = await GetLatestWorkflowsRun.GetHistory(itemsPerPage, offset, WORKFLOW_IDS);

      const runs = Array.isArray(response.runs) ? response.runs : [];
      setHistory(runs);

      const declared = response.total;
      const minTotal = offset + runs.length;
      if (typeof declared === 'number' && !Number.isNaN(declared) && declared >= minTotal) {
        setTotalItems(declared);
      } else if (runs.length === itemsPerPage) {
        setTotalItems(Math.max(minTotal, minTotal + 1));
      } else {
        setTotalItems(minTotal);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteWorkflowRun(deleteConfirm.runId);
      setDeleteConfirm({ show: false, runId: null });
      showToast("Workflow deleted successfully!", "success");

      const newTotal = totalItems - 1;
      setTotalItems(newTotal);
      const newTotalPages = Math.ceil(newTotal / itemsPerPage);

      if (currentPage > newTotalPages && currentPage > 1) {
        setCurrentPage(newTotalPages);
      } else {
        fetchHistory(currentPage);
      }
    } catch (error) {
      showToast("Failed to delete workflow.", "danger");
    }
  };

  const handleDeleteRequest = (runId) => setDeleteConfirm({ show: true, runId });

  const handleExplore = async (item) => {
    setLoadingExploreId(item.id);
    try {
      const workflowConfig = await buildConfigFromItem(item);
      
      if (!workflowConfig) {
        console.error('Could not build workflow config');
        showToast('Failed to load workflow configuration', 'danger');
        return;
      }
      
      const allMessages = item.outputs?.messages || [];

      if (item.status === 'waiting') {
        await setRunStateForFeedback(item.id, workflowConfig, allMessages);
      } else {
        await setRunStateForDownload(item.id, workflowConfig, allMessages);
      }
      navigate('/run-progress');
    } catch (error) {
      console.error("Error navigating to run progress:", error);
      showToast('Failed to explore workflow', 'danger');
    } finally {
      setLoadingExploreId(null);
    }
  };


  const buildConfigFromItem = async (item) => {
    const workflowId = item.workflow?.id;
    const inputs = item.inputs?.inputs || {};
    
    const outputLanguage = inputs.Output_Language || inputs.Output_language;
    const compliance = inputs.ComplianceCheck || inputs.Compliance || 'none';
    const emailTemplateFromRun = inputs.Email_Template || inputs.Email_template;
    const emailTemplateRaw = emailTemplateFromRun || 'standard';
    
    const templateReverseMap = {
      'Template 1': 'template1',
      'Template 2': 'template2',
      'Template 3': 'template3',
      'standard': 'template1',
      'Template Standard MIA': 'Template Standard MIA',
      'Template 1 Onco BR': 'Template 1 Onco BR',
      'Template 1 Onco MX': 'Template 1 Onco MX',
    };
    const emailTemplate = templateReverseMap[emailTemplateRaw] || emailTemplateFromRun || 'Template Standard MIA';
    
    let formatKey = '';

    for (const [key, id] of Object.entries(WORKFLOW_MATRIX)) {
      if (id === workflowId) {
        const parts = key.split('-');
        if (parts.length >= 2) {
          formatKey = parts[1];
          break;
        }
      }
    }

    if (!formatKey) {
      const workflowName = item.workflow?.name?.toLowerCase() || '';
      if (workflowName.includes('email')) formatKey = 'email';
      else if (workflowName.includes('news') || workflowName.includes('newsletter')) formatKey = 'news';
      else if (workflowName.includes('separata')) formatKey = 'separata';
      else if (workflowName.includes('whatsapp') || workflowName.includes('wtpp')) formatKey = 'whatsapp';
    }
    
    if (!formatKey) {
      console.error('Could not determine format for workflow:', item.workflow);
      return null;
    }
    
    const nodeTitles = extractOrderedRunNodes(item);
    
    const selectedConfig = getWorkflowConfig(formatKey, workflowId, nodeTitles);
    
    if (!selectedConfig) {
      console.error('Could not get workflow config for:', formatKey, workflowId);
      return null;
    }

    const includePtBrContext =
      outputLanguage === 'Portuguese' && compliance === 'Brazil';

    const storedBrand =
      inputs.Brand ||
      inputs.Brand_name ||
      inputs.Product ||
      inputs.Product_brand ||
      inputs.product ||
      '';

    return {
      ...selectedConfig,
      selectedFormat: formatKey,
      outputLanguage: outputLanguage,
      compliance: compliance,
      ...(includePtBrContext ? { context: inputs.Context || '' } : {}),
      ...(includePtBrContext && storedBrand ? { brand: storedBrand } : {}),
      fileId: inputs.Content_File?.file_id || inputs.Content?.file_id || inputs.Content_input?.file_id || inputs.Input_Content?.file_id,
      fileName: inputs.Content_File?.file_name || inputs.Content?.file_name || inputs.Content_input?.file_name || inputs.Input_Content?.file_name,
      workflowNodes: nodeTitles,
      data: {
        fileId: inputs.Content_File?.file_id || inputs.Content?.file_id || inputs.Content_input?.file_id || inputs.Input_Content?.file_id,
        fileName: inputs.Content_File?.file_name || inputs.Content?.file_name || inputs.Content_input?.file_name || inputs.Input_Content?.file_name,
        outputLanguage: outputLanguage,
        compliance: compliance,
        ...(includePtBrContext ? { context: inputs.Context || '' } : {}),
        ...(includePtBrContext && storedBrand ? { brand: storedBrand } : {}),
        emailSettings: { template: emailTemplate },
        selectedFormat: formatKey,
        selectedFormatLabel: formatKey.charAt(0).toUpperCase() + formatKey.slice(1)
      }
    };
  };

  const truncateText = (text, limit = 120) => text.length > limit ? text.substring(0, limit) + "..." : text;

  const handleCreateNewContent = () => createModal.open();

  const getStatusBadge = (status) => {
    const statusMap = {
      'succeeded': { label: 'SUCCEEDED', color: 'success' },
      'waiting': { label: 'WAITING', color: 'waiting' },
      'stopped': { label: 'STOPPED', color: 'stopped' },
      'failed': { label: 'FAILED', color: 'danger' },
      'running': { label: 'RUNNING', color: 'running' }
    };
    const statusInfo = statusMap[status.toLowerCase()] || { label: status, color: 'default' };
    return <span className={`status-badge status-badge--${statusInfo.color}`}>{statusInfo.label}</span>;
  };

  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePageChange = (page) => {
    if (isLoading || page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;

      if (totalPages <= maxVisible) {
        for (let i = 1; i <= totalPages; i++) pages.push(i);
      } else {
        if (currentPage <= 3) {
          pages.push(1, 2, 3, 4, '...', totalPages);
        } else if (currentPage >= totalPages - 2) {
          pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
        } else {
          pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
        }
      }
      return pages;
    };

    return (
      <div className={`pagination ${isLoading ? 'pagination--loading' : ''}`}>
        <div className="pagination__info">
          Showing <strong>{startItem}</strong> to <strong>{endItem}</strong> of <strong>{totalItems}</strong> results
        </div>
        <div className="pagination__controls">
          <button
            className="pagination__button"
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1 || isLoading}
            title="First page"
          >
            <FiChevronsLeft size={18} />
          </button>
          <button
            className="pagination__button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || isLoading}
            title="Previous page"
          >
            <FiChevronLeft size={18} />
          </button>
          <div className="pagination__numbers">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="pagination__ellipsis">...</span>
              ) : (
                <button
                  key={page}
                  className={`pagination__number ${currentPage === page ? 'pagination__number--active' : ''}`}
                  onClick={() => handlePageChange(page)}
                  disabled={isLoading || currentPage === page}
                >
                  {page}
                </button>
              )
            ))}
          </div>
          <button
            className="pagination__button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || isLoading}
            title="Next page"
          >
            <FiChevronRight size={18} />
          </button>
          <button
            className="pagination__button"
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || isLoading}
            title="Last page"
          >
            <FiChevronsRight size={18} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="content-list-page">
      <div className="content-list-page__container">
        <div className="content-list-page__header">
          <button className="button button--large button-new-content" onClick={handleCreateNewContent}>
            <span className="button__icon button__icon--left"><IoMdAddCircleOutline size={24} /></span>
            <span className="button__text">Create new content</span>
          </button>
        </div>
        <div className="content-list-page__main">
          <div className="content-list-page__section">
            <div className="content-list-page__section-header">
              <h2 className="content-list-page__section-title">
                Recent content
              </h2>
            </div>
            <div className="content-list-page__section-content">
              {isLoading ? (
                <div className="content-list-page__loading">Loading...</div>
              ) : history.length > 0 || totalItems > 0 ? (
                <>
                  <div className="table-container">
                    <table className="runs-table">
                      <tbody>
                        {history.map((item) => {
                          const inputs = item.inputs?.inputs || {};

                          const fileName = inputs.Content_File?.file_name 
                                        || inputs.Content?.file_name 
                                        || inputs.Content_input?.file_name;

                          const outputLanguage = inputs.Output_Language || inputs.Output_language;
                          const allMessages = item.outputs?.messages || [];
                          const lastContent = allMessages.length > 0 ? allMessages[allMessages.length - 1].content : "";
                          const nodeTitle = item.outputs?.node_title || "Processing";
                          
                          return (
                            <tr key={item.id} className="run-card-row">
                              <td>
                                <div className="column-internal-wrapper">
                                  <span className="column-label">Workflow</span>
                                </div>
                                <div className="workflow-cell">
                                  <div className="workflow-name">
                                    {
                                      item.workflow?.name
                                    }
                                  </div>
                                  <div className="workflow-meta">
                                    Started: {new Date(item.started_at).toLocaleDateString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                    <span className="workflow-cost">Cost: ${item.total_cost?.toFixed(4) || "0.0000"}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="column-internal-wrapper">
                                  <span className="column-label">Information source</span>
                                  <div className="input-variables">
                                    {(fileName) && (
                                      <div className="input-row">
                                        <span className="input-label">Content</span>
                                        <span className="input-value">
                                          {fileName}
                                        </span>
                                      </div>
                                    )}
                                    {outputLanguage && (
                                      <div className="input-row">
                                        <span className="input-label">Output_Language</span>
                                        <span className="input-value">{outputLanguage}</span>
                                      </div>
                                    )}
                                    {item.inputs?.inputs?.Email_template && (
                                      <div className="input-row">
                                        <span className="input-label">Email_template</span>
                                        <span className="input-value">{item.inputs.inputs.Email_template}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="column-internal-wrapper">
                                  <span className="column-label">Results</span>
                                  <div className="output-cell">
                                    {lastContent ? (
                                      <>
                                        <div className="output-node">Node: {nodeTitle}</div>
                                        <div className="output-content">{truncateText(lastContent)}</div>
                                      </>
                                    ) : (
                                      <span className="output-empty">No output yet</span>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td>{getStatusBadge(item.status)}</td>
                              <td>
                                <div className="action-buttons">
                                  {lastContent && (item.status === 'waiting' || item.status === 'succeeded') && (
                                    <button 
                                      className="action-btn action-btn--view"
                                      onClick={() => handleExplore(item)}
                                      title="Explore"
                                      disabled={loadingExploreId === item.id}
                                    >
                                      <FaArrowRight size={18} /> {loadingExploreId === item.id ? 'Loading...' : 'Explore'}
                                    </button>
                                  )}
                                  <button 
                                    className="action-btn action-btn--delete"
                                    onClick={() => handleDeleteRequest(item.id)}
                                    title="Delete"
                                  >
                                    <MdDelete size={18} /> Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {renderPagination()}
                </>
              ) : (
                <div className="content-list-page__empty">
                  <p className="content-list-page__empty-text">
                    You have no recent content. <br />
                    Get started by clicking 'Create New Content!'
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {viewFullModal.show && (
        <div className="modal-overlay" onClick={() => setViewFullModal({ show: false, content: '' })}>
          <div className="full-content-modal" onClick={e => e.stopPropagation()}>
            <div className="full-content-modal__header">
              <h3>Full Generated Content</h3>
              <button className="close-modal" onClick={() => setViewFullModal({ show: false, content: '' })}>
                <MdClose size={24} />
              </button>
            </div>
            <div className="full-content-modal__body">{viewFullModal.content}</div>
          </div>
        </div>
      )}

      {deleteConfirm.show && (
        <div className="modal-confirm-overlay">
          <div className="modal-confirm-card">
            <div className="modal-confirm-icon"><MdDelete size={48} /></div>
            <h3>Delete run</h3>
            <p>Are you sure you want to delete this run? This action cannot be undone.</p>
            <div className="modal-confirm-actions">
              <button className="btn-cancel" onClick={() => setDeleteConfirm({ show: false, runId: null })}>
                Cancel
              </button>
              <button className="btn-confirm" onClick={confirmDelete}>Delete run</button>
            </div>
          </div>
        </div>
      )}

      {notification.show && (
        <div className={`toast-message toast-message--${notification.type}`}>
          <MdCheckCircle size={20} />
          <span>{notification.message}</span>
        </div>
      )}

      <CreateContentModal 
        isOpen={createModal.isOpen} 
        onClose={createModal.close} 
        onOpenEmailSettings={emailModal.open} 
        initialData={createModal.data} 
      />
      <EmailSettingsModal 
        isOpen={emailModal.isOpen} 
        onClose={emailModal.close} 
        contentData={emailModal.data} 
        onBack={() => { emailModal.close(); createModal.open(emailModal.data); }} 
        initialData={emailModal.data}
        onStartRun={handleEmailStartRun}
      />
    </div>
  );
};